import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Box,
  Button,
  FormHelperText,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useEntityPermission, useLocalization } from '@ui-framework/index';
import IconLibrary from '../IconLibrary';

const PresignedFileUpload = forwardRef(
  (
    {
      field = {},
      name = field.name,
      onChange,
      onBlur,
      permission,
      error,
      accept = field.accept,
      extensions = field.extensions || [],
      maxSizeMb = field.maxSizeMb,
      currentFileName = field.currentFileName,
      downloadUrl = field.downloadUrl,
      progress = field.progress,
      uploadError = field.uploadError,
      disabled = false,
    },
    ref,
  ) => {
    const form = useFormContext();
    const value = form?.watch(name);
    const { formatMessage } = useLocalization();
    const { isReadOnly, isHidden } = useEntityPermission();
    const [validationError, setValidationError] = useState(null);
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => inputRef.current);
    const readOnly = field.readOnly || isReadOnly(permission, value);
    const normalizedExtensions = extensions.map(extension =>
      extension.toLowerCase().replace(/^\./, ''),
    );
    const stageFile = event => {
      const file = event.target.files?.[0];
      if (!file) return;
      let message = null;
      if (
        normalizedExtensions.length &&
        !normalizedExtensions.includes(file.name.split('.').pop().toLowerCase())
      ) {
        message = formatMessage(
          {
            id: 'UPLOAD_FILE_TYPE_INVALID',
            defaultMessage:
              'Choose a file with one of these extensions: {extensions}',
          },
          { extensions: normalizedExtensions.join(', ') },
        );
      } else if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) {
        message = formatMessage(
          {
            id: 'CHOOSE_A_FILE_SMALLER_THAN_MAXSIZEMB_MB',
            defaultMessage: 'Choose a file smaller than {maxSizeMb} MB.',
          },
          { maxSizeMb },
        );
      }
      setValidationError(message);
      if (message) {
        const input = event.target;
        input.value = '';
        return;
      }
      if (form) {
        form.setValue(name, file, { shouldDirty: true, shouldValidate: true });
        // Cross-field rules (PDF or video) may sit on a sibling field.
        if (form.formState.isSubmitted) form.trigger();
      } else onChange?.(file);
    };
    if (isHidden(permission)) return null;
    const message =
      validationError || uploadError?.message || uploadError || error;
    const fileName = value?.name || currentFileName;
    return (
      <Box>
        <Stack spacing={1} alignItems="flex-start">
          {!readOnly && (
            <>
              <input
                ref={inputRef}
                id={name}
                name={name}
                type="file"
                accept={
                  accept ||
                  normalizedExtensions
                    .map(extension => `.${extension}`)
                    .join(',')
                }
                disabled={disabled}
                style={{ display: 'none' }}
                onBlur={event => {
                  if (form)
                    form.setValue(name, form.getValues(name), {
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                  else onBlur?.(event);
                }}
                onChange={stageFile}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<IconLibrary.Upload />}
                disabled={disabled}
                onClick={() => inputRef.current?.click()}>
                {formatMessage({
                  id: 'CHOOSE_FILE',
                  defaultMessage: 'Choose file',
                })}
              </Button>
            </>
          )}
          {fileName &&
            (downloadUrl && !value?.name ? (
              <Link
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer">
                {fileName}
              </Link>
            ) : (
              <Typography variant="body2">
                {fileName}
                {value?.size != null
                  ? ` (${(value.size / 1024 / 1024).toFixed(2)} MB)`
                  : ''}
              </Typography>
            ))}
        </Stack>
        {typeof progress === 'number' && (
          <LinearProgress variant="determinate" value={progress} />
        )}
        {message && <FormHelperText error>{message}</FormHelperText>}
      </Box>
    );
  },
);

export default PresignedFileUpload;
